import { Box, Alert, FormHelperText, IconButton, InputAdornment, Typography, TextField } from '@mui/material';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import PasswordHint from '../utils/passwordHint';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Iconify from '../components/Iconify';
import { useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import Page from "../components/Page";
import { useFormik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import axios from 'axios';
import '../css/changePassword.css';

const ChangePassword = () => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const AuthenticatedUser = useSelector(state => state.AuthenticatedUser);

  const [buttonLoading, setButtonLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const clearLocalStorageKies = () => {
    // List of keys to remove
    const keysToRemove = ['token', 'filters', 'i18nextLng'];

    // Remove specific keys
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  };

  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object().shape({
      oldPassword: Yup.string().required(t('oldPasswordRequired')),
      newPassword: Yup.string()
        .trim()
        .min(12, t('passwordMinLength'))
        .matches(/[a-z]/, t('passwordLowercase'))
        .matches(/[A-Z]/, t('passwordUppercase'))
        .matches(/[0-9]/, t('passwordNumber'))
        .matches(/[!@#$%^&*.]/, t('passwordSpecialChar'))
        .required(t('passwordRequired')),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], t('passwordsMustMatch'))
        .required(t('confirmPasswordRequired')),
    }),
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      setButtonLoading(true);
      setErrorMsg(null);
      try {
        const { status } = await axios.put(`user/changePass/${AuthenticatedUser.id}`, values);
        if (status === 200 || status === 201 || status === 202 || status === 204) {
          clearLocalStorageKies();
          // Remove the token from axios headers
          delete axios.defaults.headers.common["Authorization"];
          navigate("/login");
        }
      } catch (err) {
        setErrorMsg(err.message);
        console.error('Error change password:', err);
        CustomErrorLogger(err, 'error change password in ChangePassword');
      } finally {
        setButtonLoading(false);
      }
    },
  });

  const { handleSubmit, values, handleChange, handleBlur, touched, errors, isValid } = formik;

  return (
    <Page title={t("Change_Password_Page_Title")}>

      <form onSubmit={handleSubmit} className="CP_form">

        <Typography className="CP_dialog_title">{t('changePassword')}</Typography>
        <Typography className="CP_dialog_SubTitle">{t('passwordHint')}</Typography>

        {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

        <Box>
          <FormHelperText id="outlined-weight-helper-text" className="CP_input_title">{t('oldPassword')}</FormHelperText>
          <TextField
            id="ForgotPassOldPassword"
            name="oldPassword"
            type={showOldPassword ? "text" : "password"}
            fullWidth
            required
            variant="outlined"
            className="customTextField"
            value={values.oldPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.oldPassword && Boolean(errors.oldPassword)}
            helperText={touched.oldPassword && errors.oldPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowOldPassword((show) => !show)} edge="end">
                    <Iconify
                      icon={
                        showOldPassword
                          ? "eva:eye-fill"
                          : "eva:eye-off-fill"
                      }
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box>
          <FormHelperText id="outlined-weight-helper-text" className="CP_input_title">{t('newPassword')}</FormHelperText>
          <TextField
            id="ForgotPassNewPassword"
            name="newPassword"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            required
            variant="outlined"
            className="customTextField"
            value={values.newPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.newPassword && Boolean(errors.newPassword)}
            helperText={touched.newPassword && errors.newPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword((show) => !show)} edge="end">
                    <Iconify
                      icon={
                        showNewPassword
                          ? "eva:eye-fill"
                          : "eva:eye-off-fill"
                      }
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box>
          <FormHelperText id="outlined-weight-helper-text" className="CP_input_title">{t('confirmPassword')}</FormHelperText>
          <TextField
            id="ForgotPassConfirmPassword"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            required
            variant="outlined"
            className="customTextField"
            color={values.confirmPassword === values.newPassword ? "success" : "error"}
            value={values.confirmPassword}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.confirmPassword && Boolean(errors.confirmPassword)}
            helperText={touched.confirmPassword && errors.confirmPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmPassword((show) => !show)} edge="end">
                    <Iconify
                      icon={
                        showConfirmPassword
                          ? "eva:eye-fill"
                          : "eva:eye-off-fill"
                      }
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {values?.newPassword && <PasswordHint password={values?.newPassword} />}

        <LoadingButton fullWidth loading={buttonLoading} className='CP_button_valider' type="submit" variant="contained" disabled={!isValid || buttonLoading} >{t("saveNewPassword")}</LoadingButton>

      </form>

    </Page >
  );
}

export default ChangePassword;